<template>
  <div class="space-y-2">
    <!-- Champ de recherche -->
    <div class="relative">
      <input
        v-model.trim="searchTerm"
        type="text"
        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
        placeholder="Rechercher un acheteur..."
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
      />
      
      <!-- Dropdown des résultats -->
      <div
        v-if="showDropdown"
        class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
      >
        <!-- Loader -->
        <div v-if="isLoading" class="px-4 py-3 text-center">
          <div class="flex items-center justify-center space-x-2">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
            <span class="text-sm text-black">Recherche en cours...</span>
          </div>
        </div>
        
        <!-- Résultats -->
        <div v-else-if="suggestions.length > 0">
          <div
            v-for="suggestion in suggestions"
            :key="suggestion.id"
            @click="selectBuyer(suggestion)"
            class="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
          >
            <div class="font-medium text-black">{{ suggestion.label }}</div>
            <div class="text-sm text-black">{{ suggestion.email }}</div>
            <div class="text-xs text-black">{{ suggestion.desc }}</div>
          </div>
        </div>
        
        <!-- Aucun résultat -->
        <div v-else-if="!isLoading && searchTerm.length > 0" class="px-4 py-3 text-center text-black">
          Aucun acheteur trouvé
        </div>
      </div>
    </div>
    
    <!-- Acheteur(s) sélectionné(s) -->
    <template v-if="multiple">
      <div v-if="selectedBuyers.length" class="flex flex-wrap gap-2">
        <span
          v-for="buyer in selectedBuyers"
          :key="buyer.id"
          class="inline-flex items-center bg-gray-100 text-black px-2 py-1 rounded-full border border-gray-300"
        >
          <span class="text-sm mr-1">{{ buyer.label }}</span>
          <button type="button" class="text-black hover:text-black" @click="removeBuyer(buyer.id)">
            <i class="fas fa-times"></i>
          </button>
        </span>
      </div>
    </template>
    <template v-else>
      <div v-if="selectedBuyer" class="flex items-center justify-between p-3 bg-gray-50 border border-gray-300 rounded-md">
        <div>
          <div class="font-medium text-black">{{ selectedBuyer.label }}</div>
          <div class="text-sm text-black">{{ selectedBuyer.email }}</div>
          <div class="text-xs text-black">{{ selectedBuyer.desc }}</div>
        </div>
        <button
          type="button"
          @click="clearSelection"
          class="text-black hover:text-black"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { searchBuyersForBlocking } from '@/api'
import type { BuyerAutocomplete } from '@/types'

interface Props {
  modelValue?: number | number[] | null
  multiple?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: number | number[] | null): void
}

const props = withDefaults(defineProps<Props>(), {
  multiple: false
})
const emit = defineEmits<Emits>()

// État local
const searchTerm = ref('')
const suggestions = ref<BuyerAutocomplete[]>([])
const showDropdown = ref(false)
const selectedBuyer = ref<BuyerAutocomplete | null>(null)
const selectedBuyers = ref<BuyerAutocomplete[]>([])
const isLoading = ref(false)
const searchTimeout = ref<number | null>(null)

// Charger les acheteurs lors de la recherche avec debounce
const searchBuyers = async (term: string) => {
  if (isLoading.value || term.length < 1) return
  
  isLoading.value = true
  try {
    suggestions.value = await searchBuyersForBlocking(term)
  } catch (error) {
    suggestions.value = []
  } finally {
    isLoading.value = false
  }
}

// Gestionnaires d'événements
const handleInput = () => {
  // Réinitialiser la sélection
  if (!props.multiple) {
    if (selectedBuyer.value) {
      selectedBuyer.value = null
      emit('update:modelValue', null)
    }
  }
  
  // Annuler le timeout précédent
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
  }
  
  if (searchTerm.value.length >= 1) {
    // Debounce de 300ms pour éviter trop d'appels API
    searchTimeout.value = setTimeout(() => {
      searchBuyers(searchTerm.value)
      showDropdown.value = true
    }, 300)
  } else {
    suggestions.value = []
    showDropdown.value = false
  }
}

const handleFocus = () => {
  if (searchTerm.value.length >= 1 && suggestions.value.length > 0) {
    showDropdown.value = true
  }
}

const handleBlur = () => {
  // Délai pour permettre le clic sur un élément
  setTimeout(() => {
    showDropdown.value = false
  }, 200)
}

const selectBuyer = (buyer: BuyerAutocomplete) => {
  if (props.multiple) {
    // Prevent duplicates
    if (!selectedBuyers.value.find(b => b.id === buyer.id)) {
      selectedBuyers.value.push(buyer)
      emit('update:modelValue', selectedBuyers.value.map(b => b.id))
    }
    searchTerm.value = ''
    suggestions.value = []
    showDropdown.value = false
  } else {
    selectedBuyer.value = buyer
    searchTerm.value = ''
    suggestions.value = []
    showDropdown.value = false
    emit('update:modelValue', buyer.id)
  }
}

const removeBuyer = (id: number) => {
  selectedBuyers.value = selectedBuyers.value.filter(b => b.id !== id)
  // In multiple mode, always emit an array (empty array when none selected)
  emit('update:modelValue', selectedBuyers.value.map(b => b.id))
}

const clearSelection = () => {
  if (props.multiple) {
    selectedBuyers.value = []
    searchTerm.value = ''
    suggestions.value = []
    // Emit empty array to keep parent model type stable (number[])
    emit('update:modelValue', [])
  } else {
    selectedBuyer.value = null
    searchTerm.value = ''
    suggestions.value = []
    emit('update:modelValue', null)
  }
}

// Fermer le dropdown en cliquant ailleurs
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement
  if (!target.closest('.relative')) {
    showDropdown.value = false
  }
}

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  // Nettoyer le timeout si le composant est démonté
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
  }
})

// Exposer la fonction de nettoyage
defineExpose({
  clearSelection
})
</script>

<style scoped>
.relative {
  position: relative;
}
</style>

